import React, { useRef, useState } from 'react';
import { Area, AreaChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import moment from 'moment';
import {
    AREA_FILL_COLOR_DEFINITION,
    AREA_FILL_COLOR_DEFINITION_IDENTIFIER,
    getXAxisTicks,
    xAxisFormatter,
    yAxisFormatter
} from './GraphBuilder';
import InsightGraphFilter, { INSIGHT_GRAPH_FILTER_OPTIONS } from './InsightGraphFilter';
import { useIspmContext } from '../../../IspmContext';
import useGetInsightTimeline from '../../../api/queries/useGetInsightTimeline';
import useGetRefSize from '../../../../../hooks/useGetRefSize';
import { AZURE_ENVIRONMENT } from '../../../constants';

const InsightGraphCard = () => {
    const { selectedInsight, domainFilter, insightsData } = useIspmContext();
    const [filterOffsetInDays, setFilterOffsetInDays] = useState(INSIGHT_GRAPH_FILTER_OPTIONS[1].value);
    const { data: insightTimeline, isFetching } = useGetInsightTimeline({
        ruleName: selectedInsight ?? '',
        diffInDays: filterOffsetInDays,
        sourceType: AZURE_ENVIRONMENT,
        domainName: domainFilter
    });
    const graphRef = useRef(null);
    const { height: graphHeight, width: graphWidth } = useGetRefSize(graphRef);

    const timelineIncludingToday = [
        ...(insightTimeline ?? []),
        { timestamp: Date.now(), count: insightsData?.insightsStatistics?.[selectedInsight ?? ''] }
    ];
    timelineIncludingToday.sort((a, b) => a.timestamp - b.timestamp);
    const endDate = insightTimeline?.length
        ? moment(timelineIncludingToday[insightTimeline.length - 1].timestamp)
        : moment();
    const ticks = getXAxisTicks(moment().subtract(filterOffsetInDays, 'days'), endDate);
    const xDomain = [ticks[0], endDate];

    return (
        <div className="h-full w-full p-4 rounded-xl border flex flex-col gap-[20px]">
            <div className="h-8 w-full flex items-center justify-between">
                <div className="text-14 text-sf-gray-800 font-sf font-medium ml-[45px]">Exposed Entities</div>
                <InsightGraphFilter selectedOption={filterOffsetInDays} onChange={setFilterOffsetInDays} />
            </div>
            <div className="h-[calc(100%-32px-20px)] w-full" ref={graphRef}>
                {!!timelineIncludingToday?.length && !isFetching && (
                    <AreaChart width={graphWidth} height={graphHeight} data={timelineIncludingToday}>
                        {AREA_FILL_COLOR_DEFINITION}
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="timestamp"
                            type="number"
                            scale="time"
                            domain={xDomain as any}
                            tickFormatter={xAxisFormatter}
                            tick={{
                                fontSize: 10,
                                fontFamily: '"Helvetica Neue"',
                                color: '#808080'
                            }}
                            ticks={ticks.map((tick) => tick.valueOf())}
                            tickSize={ticks.length}
                            strokeWidth={0}
                            tickMargin={10}
                        />
                        <YAxis
                            dataKey="count"
                            tickFormatter={yAxisFormatter}
                            tick={{
                                fontSize: 10,
                                fontFamily: '"Helvetica Neue"',
                                color: '#808080'
                            }}
                            tickCount={10}
                            strokeWidth={0}
                            allowDecimals={false}
                        />
                        <Tooltip labelFormatter={(timestamp) => moment(timestamp).format('L')} />
                        <Area
                            type="monotone"
                            dataKey="count"
                            stroke="#0E7FDC"
                            fill={`url(#${AREA_FILL_COLOR_DEFINITION_IDENTIFIER})`}
                            strokeWidth={1.8}
                        />
                    </AreaChart>
                )}
            </div>
        </div>
    );
};

export default InsightGraphCard;
